import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resolveBanner, type BannerVariant } from "./pageBannerConfig";
import { BannerMotif } from "./banners/motifs";

interface Props {
  overrideTitle?: string;
  overrideSubtitle?: string;
  overrideEyebrow?: string;
  variant?: BannerVariant;
}

export function PageBanner({
  overrideTitle,
  overrideSubtitle,
  overrideEyebrow,
  variant: variantProp,
}: Props = {}) {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);
  const entry = resolveBanner(pathname);

  if (!entry && !overrideTitle) return null;

  const variant: BannerVariant = variantProp ?? entry?.variant ?? "default";
  const k = entry?.key;

  const eyebrow = overrideEyebrow ?? (k ? t(`banner.${k}.eyebrow`, "") : "");
  const title = overrideTitle ?? (k ? t(`banner.${k}.title`, "") : "");
  const subtitle = overrideSubtitle ?? (k ? t(`banner.${k}.subtitle`, "") : "");

  if (!title) return null;

  return (
    <section
      aria-label={title}
      className="relative isolate overflow-hidden border-b border-border bg-card"
    >
      {/* Base gradients */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(80% 60% at 15% 20%, hsl(var(--primary) / 0.18), transparent 60%), radial-gradient(70% 60% at 85% 90%, hsl(var(--accent) / 0.18), transparent 60%)",
        }}
      />
      {/* Animated mesh blobs */}
      <div
        aria-hidden="true"
        className="absolute -left-16 top-0 -z-10 h-56 w-56 rounded-full motion-safe:animate-drift"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, hsl(var(--primary) / 0.55), transparent 65%)",
          filter: "blur(40px)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute -right-20 bottom-0 -z-10 h-64 w-64 rounded-full motion-safe:animate-drift"
        style={{
          background:
            "radial-gradient(circle at 70% 70%, hsl(var(--accent) / 0.5), transparent 65%)",
          filter: "blur(48px)",
          animationDelay: "1.5s",
        }}
      />
      {/* Dotted grid */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 opacity-[0.08]"
        style={{
          backgroundImage:
            "radial-gradient(hsl(var(--foreground)) 1px, transparent 1px)",
          backgroundSize: "16px 16px",
        }}
      />

      <div className="container flex min-h-[140px] items-center justify-between gap-6 py-8 md:min-h-[180px] md:py-12">
        <div className="min-w-0 max-w-3xl">
          {eyebrow && (
            <div lang={lang} className="mb-2 inline-flex items-center rounded-full border border-border bg-background/60 px-3 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground backdrop-blur">
              {eyebrow}
            </div>
          )}
          <h1 className="text-balance text-3xl font-semibold tracking-tight text-foreground md:text-4xl lg:text-5xl">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-3 max-w-2xl text-pretty text-sm text-muted-foreground md:text-base">
              {subtitle}
            </p>
          )}
        </div>
        <BannerMotif variant={variant} />
      </div>

      {/* Bottom fade into page background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-b from-transparent to-background"
      />
    </section>
  );
}
